// SiloIconArtwork — Jun 2026 (Randy pt-home-icon-fidelity).
//
// "Let me use the artwork that's already behind my Public Q&A as the icon."
// The images already live on the silo (cube faces + silo art) and are
// already served as real bytes from /api/silo-images/<id>, so this is a
// picker + crop, not new infrastructure.
//
// Photographic art collapses into mud at icon size, so the creator gets two
// legibility dials — a dark scrim and an optional monogram overlay. Both are
// optional on purpose: some artwork reads fine bare.
import { useEffect, useState } from "react";
import { Image as ImageIcon, Spinner, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LABELS } from "@/lib/siloCubeFaces";
import { renderArtworkIcon, loadImage } from "@/lib/iconPresets";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SCRIM_OPTIONS = [
  { id: "none", label: "None" },
  { id: "soft", label: "Soft" },
  { id: "strong", label: "Strong" },
];

export default function SiloIconArtwork({ siloId, letter, onApplied }) {
  const [images, setImages] = useState(null);
  const [picked, setPicked] = useState(null);
  const [scrim, setScrim] = useState("soft");
  const [monogram, setMonogram] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/silos/${siloId}/images`);
        setImages(r.data?.images || []);
      } catch {
        setImages([]);
      }
    })();
  }, [siloId]);

  useEffect(() => {
    if (!picked) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const img = await loadImage(`${BACKEND_URL}/api/silo-images/${picked}`);
        if (!cancelled) setPreview(renderArtworkIcon(img, { scrim, monogram, letter }));
      } catch {
        if (!cancelled) toast.error("Could not read that image.");
      }
    })();
    return () => { cancelled = true; };
  }, [picked, scrim, monogram, letter]);

  const apply = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      await api.patch(`/silos/${siloId}`, { home_icon_data_url: preview });
      toast.success("Artwork set as the home-screen icon.");
      onApplied?.(preview);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save icon.");
    } finally { setBusy(false); }
  };

  if (images === null) {
    return (
      <div className="mt-6 pt-5 border-t hairline text-xs text-[#6B6A68] flex items-center gap-2">
        <Spinner size={12} className="animate-spin" /> Loading your artwork…
      </div>
    );
  }
  if (!images.length) {
    return (
      <div className="mt-6 pt-5 border-t hairline" data-testid="silo-icon-artwork-empty">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B6A68] mb-2">Or use your silo artwork</p>
        <p className="text-xs text-[#6B6A68]">
          No artwork yet. Generate or upload a cover image for this silo and it'll show up here.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 pt-5 border-t hairline" data-testid="silo-icon-artwork">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1">Or use your silo artwork</p>
      <p className="text-xs text-[#6B6A68] mb-3 max-w-2xl">
        The same art that dresses your Public Q&amp;A, centre-cropped to a square. Busy photos lose detail
        at icon size — the dials below buy back contrast.
      </p>

      <div className="flex flex-wrap gap-2.5">
        {images.map((img) => {
          const selected = picked === img.id;
          return (
            <button
              key={img.id}
              onClick={() => setPicked(selected ? null : img.id)}
              data-testid={`silo-icon-artwork-${img.id}`}
              title={LABELS?.[img.face] || img.face || "Artwork"}
              className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                selected ? "border-[#4A5D4E] shadow-md" : "border-transparent hover:border-[#A7B3A0]"
              }`}
            >
              <img
                src={`${BACKEND_URL}/api/silo-images/${img.id}`}
                alt={img.face || "Artwork"}
                className="w-full h-full object-cover"
              />
              {selected && (
                <span className="absolute inset-0 bg-[#4A5D4E]/30 flex items-center justify-center">
                  <Check size={18} weight="bold" className="text-white" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {picked && (
        <div className="mt-4 flex items-start gap-5 flex-wrap" data-testid="silo-icon-artwork-editor">
          <div className="w-20 h-20 rounded-xl border hairline overflow-hidden bg-white flex items-center justify-center shrink-0">
            {preview ? (
              <img src={preview} alt="Icon preview" className="w-full h-full object-cover" data-testid="silo-icon-artwork-preview" />
            ) : (
              <Spinner size={14} className="animate-spin text-[#6B6A68]" />
            )}
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68] w-14">Scrim</span>
              {SCRIM_OPTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setScrim(s.id)}
                  data-testid={`silo-icon-scrim-${s.id}`}
                  className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
                    scrim === s.id
                      ? "border-[#4A5D4E] text-[#4A5D4E] bg-[#F0F1EE]"
                      : "hairline text-[#6B6A68] hover:text-[#1C1B1A]"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs text-[#1C1B1A] cursor-pointer">
              <input
                type="checkbox"
                checked={monogram}
                onChange={(e) => setMonogram(e.target.checked)}
                data-testid="silo-icon-monogram-toggle"
                className="accent-[#4A5D4E]"
              />
              Overlay the “{letter}” monogram
            </label>
            <button
              onClick={apply}
              disabled={busy || !preview}
              data-testid="silo-icon-artwork-apply"
              className="self-start text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy ? <Spinner size={12} className="animate-spin" /> : <ImageIcon size={12} weight="duotone" />}
              {busy ? "Saving…" : "Use this icon"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
